import type { ReactNode } from "react";
import type { Dashboard } from "@/types";

import { Chip } from "@heroui/react";
import { Widget } from "@heroui-pro/react";

import { aed, aedRounded } from "@/components/sales/shared";

type Column<T> = {
  label: string;
  end?: boolean;
  cell: (row: T) => ReactNode;
};

const th = "text-muted px-3 py-2 text-xs font-medium";

function RankedTable<T extends { id: string }>({
  title,
  description,
  rows,
  columns,
  empty,
}: {
  title: string;
  description: string;
  rows: T[];
  columns: Column<T>[];
  empty: string;
}) {
  return (
    <Widget>
      <Widget.Header>
        <div className="flex flex-col">
          <Widget.Title>{title}</Widget.Title>
          <Widget.Description>{description}</Widget.Description>
        </div>
      </Widget.Header>
      <Widget.Content className="p-0">
        {rows.length === 0 ? (
          <p className="text-muted py-12 text-center text-sm">{empty}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-separator border-b">
                  {columns.map((column) => (
                    <th
                      key={column.label}
                      className={`${th} ${column.end ? "text-end" : "text-start"}`}
                    >
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-separator divide-y">
                {rows.map((row) => (
                  <tr key={row.id}>
                    {columns.map((column) => (
                      <td
                        key={column.label}
                        className={`px-3 py-2.5 ${column.end ? "text-end tabular-nums whitespace-nowrap" : ""}`}
                      >
                        {column.cell(row)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Widget.Content>
    </Widget>
  );
}

const count = (n: number) => n.toLocaleString();

export function TopProductsTable({ data }: { data: Dashboard }) {
  return (
    <RankedTable
      columns={[
        {
          label: "Product",
          cell: (r) => <span className="font-medium">{r.name}</span>,
        },
        { label: "Units", end: true, cell: (r) => count(r.units) },
        { label: "Orders", end: true, cell: (r) => count(r.orders) },
        { label: "Revenue", end: true, cell: (r) => aedRounded(r.revenue) },
      ]}
      description="Top 20 by units ordered; revenue from fulfilled orders"
      empty="No orders in this range"
      rows={data.topProducts}
      title="Top products"
    />
  );
}

export function TopOutOfStockTable({ data }: { data: Dashboard }) {
  return (
    <RankedTable
      columns={[
        {
          label: "Product",
          cell: (r) => (
            <div className="flex flex-col leading-snug">
              <span className="font-medium">{r.name}</span>
              <span className="text-muted text-xs">
                {r.supplier ?? "No supplier"}
              </span>
            </div>
          ),
        },
        { label: "Units", end: true, cell: (r) => count(r.units) },
        {
          label: "Lost sales",
          end: true,
          cell: (r) => aedRounded(r.lostRevenue),
        },
        {
          label: "Most given reason",
          cell: (r) => (
            <span className="text-muted">{r.topReason ?? "None given"}</span>
          ),
        },
      ]}
      description="Top 20 by units the driver couldn't find"
      empty="Nothing went out of stock"
      rows={data.topOutOfStock}
      title="Top out-of-stock products"
    />
  );
}

export function LossItemsTable({ data }: { data: Dashboard }) {
  return (
    <RankedTable
      columns={[
        {
          label: "Product",
          cell: (r) => (
            <span className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{r.name}</span>
              {r.unpricedUnits > 0 ? (
                <Chip
                  className="whitespace-nowrap"
                  color="warning"
                  size="sm"
                  variant="soft"
                >
                  {`Sell price not entered × ${r.unpricedUnits}`}
                </Chip>
              ) : null}
            </span>
          ),
        },
        { label: "Units", end: true, cell: (r) => count(r.units) },
        { label: "Avg buy", end: true, cell: (r) => aed(r.avgBuyPrice) },
        { label: "Avg sell", end: true, cell: (r) => aed(r.avgSellPrice) },
        {
          label: "Profit",
          end: true,
          cell: (r) => (
            <span className={r.profit < 0 ? "text-danger" : undefined}>
              {aed(r.profit)}
            </span>
          ),
        },
      ]}
      description="Bought for more than the customer paid, worst first"
      empty="Nothing sold below its buy price"
      rows={data.lossItems}
      title="Sold at a loss"
    />
  );
}
